/**
 * Which `CanonicalDataType.Array` paths hold numbers a plot can chart.
 *
 * `Array` is a coarse canonical type: ingestion stamps it on every sequence
 * field, so one canonical type covers a 7-joint `float64[7]`, a point cloud's
 * `uint8[]` payload, and an array of nested messages. The element type is only
 * recoverable from the source-schema spelling in `data_type`, which is what
 * this module reads.
 *
 * Which ingestion produced a topic decides whether this module sees it at all,
 * and the coverage is uneven:
 *
 * - ROS bags, `ros1msg`/`ros2msg` MCAP, and OMG IDL (`omgidl`/`ros2idl`) MCAP
 *   all label their fields through one ROS-style namer, so they arrive with a
 *   bracketed ROS spelling — `float64[7]` from a `.msg`, `float64[]` from an
 *   IDL `sequence<double>`. These are the ones this distinguishes.
 * - ULog stamps numeric arrays `NumberArray`, which needs no element
 *   inspection. Only its fixed-size `bool[N]` reaches `Array`.
 * - Protobuf MCAP gets no explicit label; its numeric arrays are typed
 *   `NumberArray` and never reach this predicate.
 * - The standalone parquet ingestion action stamps `Array` on every list and
 *   records an Arrow type string (`list<item: double>`), which has no bracket
 *   to read, so a numeric list from it keeps the raw-message default. The SDK's
 *   own arrow path maps numeric lists to `NumberArray` instead.
 */
import { CanonicalDataType, type MessagePathRecord } from "./MessagePathRecord";

/**
 * Element types worth charting by default, one line per index.
 *
 * Byte-width types (`uint8`, `int8`, `byte`, `char`) are deliberately absent:
 * they overwhelmingly spell binary payloads rather than measurements —
 * `sensor_msgs/PointCloud2.data`, serialized buffers — and the plot panel
 * emits one chart dataset per array element, so charting one would try to draw
 * a line per byte.
 *
 * This is a heuristic for picking a default, NOT a bound on how many lines a
 * plot can be asked to draw. It filters element type, and nothing about an
 * element type constrains an array's length: an unbounded `float32[]` is
 * admitted here, and a 1000-beam laser scan is spelled exactly that way.
 * Nothing downstream caps the dataset count either. A byte array that really
 * is a measurement is still plottable on purpose — from the topic tree's
 * context menu, or by asking for a plot panel by name — and so is an array
 * far wider than anything worth drawing.
 *
 * These are ROS spellings, which is the whole vocabulary that reaches here:
 * ingestion labels ROS `.msg` and OMG IDL schemas alike through one ROS-style
 * namer, so an IDL `sequence<double>` arrives as `float64[]` rather than
 * carrying its own `double` spelling. Protobuf takes a different route again
 * and gets no explicit label, so its numeric arrays are typed `NumberArray`
 * and never reach this predicate.
 *
 * A source that labels its own fields would need its names added as a set, and
 * the names to add are the ones it *writes* — not the ones its schemas are
 * written in, which is a distinction IDL makes vivid.
 */
const PLOTTABLE_ELEMENT_DATA_TYPES = new Set([
  "bool",
  "float32",
  "float64",
  "int16",
  "int32",
  "int64",
  "uint16",
  "uint32",
  "uint64",
]);

/**
 * The element type spelled inside an array `data_type`, or undefined when the
 * type spells no array or names no element.
 *
 * Handles the three array spellings a source schema can carry — unbounded
 * (`float64[]`), fixed-size (`float64[7]`), and ROS 2 bounded
 * (`float64[<=10]`) — by reading everything ahead of the first bracket. A
 * namespaced message type comes back whole (`geometry_msgs/Point[]` ->
 * `geometry_msgs/Point`) so callers can tell an array of messages from an
 * array of primitives.
 */
export function arrayElementDataType(dataType: string): string | undefined {
  const bracketIndex = dataType.indexOf("[");
  if (bracketIndex === -1) {
    return undefined;
  }

  const elementType = dataType.slice(0, bracketIndex).trim();
  return elementType.length > 0 ? elementType : undefined;
}

/**
 * Whether this message path is an array of numbers a plot panel can chart,
 * one line per index.
 *
 * False for every path that is not canonically an `Array`, including the
 * `NumberArray` paths that are already known to be numeric — callers deciding
 * a panel type handle that type directly and reach here only for the coarse
 * `Array` case.
 */
export function isPlottableNumberArray(
  messagePath: MessagePathRecord,
): boolean {
  if (messagePath.canonical_data_type !== CanonicalDataType.Array) {
    return false;
  }

  const elementType = arrayElementDataType(messagePath.data_type);
  return (
    elementType !== undefined &&
    PLOTTABLE_ELEMENT_DATA_TYPES.has(elementType.toLowerCase())
  );
}
